/**
 * The browser half of a `"cell"` prompt (`board-pick-prompts` Phase 2): a decision whose answer is a
 * map space is answered on the board, never from a list. The legal spaces are the prompt's own
 * options (each carries its `cell`), so the pick can't offer a space the server won't accept.
 *
 * A multi-section placement (Earthshaker Shells' two debris segments) is asked one section per
 * prompt. Every section but the last answers on its click; the last is staged, ghosted with the
 * earlier ones, and answered by one confirm ({@link cellPickNeedsConfirm}).
 */
import { squareCell, worldFor, type EncounterState, type SquareCell } from "@lancer-vtt/domain";
import type { PromptAnswerKind, PromptOption, PromptRequest, PromptSection } from "@lancer-vtt/shared";
import type { TargetingBanner } from "./TargetingMode";
import { cellCountLine } from "./TargetingMode";

/** A board pick answering a `"cell"` prompt: its id, its legal spaces with their option ids, and its section, if it places one of several. */
export type CellPick = { promptId: string; options: { id: string; cell: SquareCell }[]; section?: PromptSection };

/** The last section's clicked space, waiting on the confirm. */
export type StagedCell = { promptId: string; optionId: string; cell: SquareCell };

/** The {@link CellPick} a prompt arms, or `null` when it is not a `"cell"` prompt. The combat board is square. */
export function cellPickFor(prompt: PromptRequest): CellPick | null {
  if (prompt.answerKind !== "cell") return null;
  const options = prompt.options.flatMap((o) => (o.cell?.grid === "square" ? [{ id: o.id, cell: squareCell(o.cell.x, o.cell.y) }] : []));
  return { promptId: prompt.promptId, options, ...(prompt.section ? { section: prompt.section } : {}) };
}

/** The option id that answers `pick` with the clicked `cell`, or `null` when the space is not legal. */
export function cellPickAnswerAt(state: EncounterState, pick: CellPick, cell: { x: number; y: number }): string | null {
  const world = worldFor(state);
  // The board hands back a bare `{x, y}`; tag it before comparing.
  const clicked = squareCell(cell.x, cell.y);
  return pick.options.find((o) => world.equals(o.cell, clicked))?.id ?? null;
}

/** Whether a click stages rather than answers: the last section of a placement of two or more. */
export function cellPickNeedsConfirm(pick: CellPick): boolean {
  return pick.section !== undefined && pick.section.count > 1 && pick.section.index === pick.section.count - 1;
}

/** The legal spaces' keys: the wash, and the stage's click gate. */
export function cellPickKeys(state: EncounterState, pick: CellPick): Set<string> {
  const world = worldFor(state);
  return new Set(pick.options.map((o) => world.key(o.cell)));
}

/** The section ghosts: the earlier sections' answers plus the staged one, `null` when there are none. */
export function cellPickGhosts(state: EncounterState, pick: CellPick, staged: StagedCell | null): { cells: string[]; size: number } | null {
  const world = worldFor(state);
  const cells = (pick.section?.placed ?? []).flatMap((c) => (c.grid === "square" ? [world.key(squareCell(c.x, c.y))] : []));
  if (staged && staged.promptId === pick.promptId) cells.push(world.key(staged.cell));
  return cells.length > 0 ? { cells, size: pick.section?.size ?? 1 } : null;
}

/**
 * The targeting banner while a cell pick is armed: the whole prompt, since no window opens for it.
 * `title` is the prompt's own sentence, which names the section ("place Debris 2 of 2").
 */
export function cellPickBanner(args: { pick: CellPick; title: string; staged: boolean }): TargetingBanner {
  const { pick, title, staged } = args;
  return { kind: "attack", title, sub: staged ? "CONFIRM, OR CLICK ANOTHER SPACE" : `CLICK A HIGHLIGHTED SPACE · ${cellCountLine(pick.options.length)}` };
}

/** What a hovered decision-card option names on the board: a space to ghost, a unit to ring, or nothing. */
export type OptionHighlight = { cell: SquareCell } | { unitId: string };

/**
 * The board highlight for a COMMS decision-card option (`board-pick-prompts` 4.1): the GM bystander
 * answers someone else's cell or unit prompt from the card's list, and hovering an option shows
 * what it names. `null` for any other kind of decision.
 */
export function optionHighlight(answerKind: PromptAnswerKind | undefined, option: PromptOption): OptionHighlight | null {
  if (answerKind === "cell") return option.cell?.grid === "square" ? { cell: squareCell(option.cell.x, option.cell.y) } : null;
  if (answerKind === "combatant" || answerKind === "combatants") return { unitId: option.id };
  return null;
}
